import { Filter, IDateRangeWithCompareFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { Last7DaysMode } from "constants/reports/last-7-days-mode";
import { ReportType } from "constants/reports/report-type";
import { DetailedSource } from "interfaces/entities/ticket";
import { IStoreState } from "interfaces/storeState";
import { createSingleReportRequestTypeBase } from "store/entities/reports/actions";
import { selectCurrentSubscription } from "store/entities/subscriptions";
import { createRequestFetchingSelector } from "store/requests/selectors";

import { isReportTypeIsDisabled } from "./helpers";
import { IReportsViewCacheExpirationTime, IReportsViewData, IReportsViewFilters } from "./interfaces";
import { IReportsViewState } from "./reducer";

export interface IWithReportsViewState {
  views: {
    reports: IReportsViewState;
  };
}

export function isInited(state: IWithReportsViewState): boolean {
  return state.views.reports.inited;
}

export function getCurrentView(state: IWithReportsViewState): ReportType {
  return state.views.reports.currentView;
}

export function getReportCacheExpirationTime(state: IWithReportsViewState, reportType: ReportType): Date {
  return state.views.reports.cacheExpirationTime[reportType] || null;
}

export function getAllReportsCacheExpirationTime(state: IWithReportsViewState): IReportsViewCacheExpirationTime {
  return state.views.reports.cacheExpirationTime;
}

// @ts-expect-error TS(2536): Type 'T' cannot be used to index type 'IReportsVie... Remove this comment to see the full error message
export function getReportData<T extends ReportType>(state: IWithReportsViewState, reportName: T): IReportsViewData[T] {
  // @ts-expect-error TS(2536): Type 'T' cannot be used to index type 'IReportsVie... Remove this comment to see the full error message
  return state.views.reports.data[reportName];
}

export function getIsReportFetching(state: IStoreState, reportName: ReportType): boolean {
  return createRequestFetchingSelector([createSingleReportRequestTypeBase(reportName)])(state);
}

export function getFilters(state: IWithReportsViewState): IReportsViewFilters {
  return state.views.reports.filters;
}

export function getFiltersOrder(state: IWithReportsViewState): Filter[] {
  return state.views.reports.filtersOrder;
}

export function getDistribution(state: IWithReportsViewState): ReportDistribution {
  return state.views.reports.distribution;
}

export function getFilterDateRange(state: IWithReportsViewState): IDateRangeWithCompareFilter {
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return state.views.reports.filters[Filter.DateRange];
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getFilterGroup(state: IWithReportsViewState) {
  return state.views.reports.filters[Filter.Group];
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getFilterAgents(state: IWithReportsViewState) {
  return state.views.reports.filters[Filter.Agent];
}

export function hasReportComparisonDisabled(state: IWithReportsViewState): boolean {
  return isReportTypeIsDisabled(getCurrentView(state));
}

export function hasReportGroupFilterComparison(state: IWithReportsViewState): boolean {
  const group = getFilterGroup(state);

  return !!(group && group.compare);
}

export function hasReportAgentFilterComparison(state: IWithReportsViewState): boolean {
  const agent = getFilterAgents(state);

  return !!(agent && agent.compare);
}

export function hasReportDateRangeFilterComparison(state: IWithReportsViewState): boolean {
  const dateRange = getFilterDateRange(state);

  return !!(dateRange && dateRange.compare);
}

export function hasReportFiltersComparison(state: IWithReportsViewState): boolean {
  if (
    hasReportDateRangeFilterComparison(state) ||
    hasReportGroupFilterComparison(state) ||
    hasReportAgentFilterComparison(state)
  ) {
    return true;
  }

  return false;
}

export function getHighlightedIds(state: IWithReportsViewState): string[] {
  return state.views.reports.data[ReportType.ScheduledReports]
    ? (state.views.reports.data[ReportType.ScheduledReports].highlightedIds ?? [])
    : [];
}

export function getIsReportSaving(state: IStoreState): boolean {
  return createRequestFetchingSelector(["VIEWS/REPORT/CREATE_REPORT"])(state);
}

export function getIsReportEditing(state: IStoreState): boolean {
  return createRequestFetchingSelector(["VIEWS/REPORT/EDIT_SCHEDULED_REPORT"])(state);
}

export function getIsReportDeleting(state: IStoreState): boolean {
  return createRequestFetchingSelector(["VIEWS/REPORT/DELETE_SCHEDULED_REPORT"])(state);
}

export function getIsReportGenerating(state: IStoreState): boolean {
  return createRequestFetchingSelector(["VIEWS/REPORT/GENERATE_REPORT"])(state);
}

export function getLast7DaysMode(state: IStoreState): Last7DaysMode {
  return state.views.reports.last7DaysMode;
}

export function selectIsHeatmapEnabled(state: IStoreState): boolean {
  const currentSubscription = selectCurrentSubscription(state);
  // @ts-expect-error TS(2322)
  return currentSubscription;
}

export function selectLastAvailableSources(state: IStoreState): DetailedSource[] {
  return state.views.reports.lastAvailableSources;
}
